import { useEffect, useState } from "react";

import { toast } from "sonner";

import { useMutation } from "@redwoodjs/web";

import { IntegrationCard } from "src/components/SettingsDialog/IntegrationCard";
import { SettingsSection } from "src/components/SettingsDialog/SettingsSection";
import { ManageModal } from "src/components/SettingsDialog/StoreSettings/ManageModal";
import { marketingIntegrationTypes } from "src/lib/constants";
import { INTEGRATION_URL_MUTATION } from "src/lib/queries/integrations";
import { cn, pluralize } from "src/lib/utils";

import { MetaConnectionDialog } from "./MetaConnectionDialog";

export function IntegrationsSection({
  manageParam,
  store,
  loading: _loading,
  isAdmin,
  sectionTarget,
  highlightedSection,
  clearManageParam,
}) {
  const integrationTypes = Object.values(marketingIntegrationTypes).filter(
    (type) => type.id !== "tiktok_ads"
  );

  const [createIntegrationUrl, { loading: loadingIntegrationUrl }] =
    useMutation(INTEGRATION_URL_MUTATION, {
      onCompleted: (data) => {
        window.open(data.integrationUrl, "_self");
      },
      onError: (error) => {
        toast.error(error.message, {
          description: "Please try again",
        });
      },
    });

  const [manageIntegration, setManageIntegration] = useState(null);
  const [metaConnectOpen, setMetaConnectOpen] = useState(false);
  useEffect(() => {
    setMetaConnectOpen(false);
  }, [store?.id]);

  useEffect(() => {
    if (manageParam && store && !manageIntegration) {
      const integration = store.children.find(
        (i) => i.type.toLowerCase() === manageParam
      );
      if (integration) {
        setManageIntegration(integration);
        clearManageParam?.();
      }
    }
  }, [clearManageParam, manageIntegration, manageParam, store]);

  const onManage = (integration) => {
    setManageIntegration(integration);
  };

  const handleIntegrationConnect = async (integrationType) => {
    if (integrationType.id === "meta_ads") {
      setMetaConnectOpen(true);
      return;
    }
    return createIntegrationUrl({
      variables: {
        input: {
          type: integrationType.id.toUpperCase(),
          parentId: store.id,
        },
      },
    });
  };

  const loading = _loading || loadingIntegrationUrl;
  return (
    <>
      <SettingsSection
        title="Integrations"
        description="Connect your ad accounts to track spend and performance alongside store sales."
        className={cn(
          "transition-[background-color,box-shadow] duration-700",
          sectionTarget === "integrations" &&
            highlightedSection === "integrations" &&
            "bg-primary/5 ring-primary/30 ring-2"
        )}
        sectionId="integrations"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {integrationTypes.map((integrationType) => {
            const connectedIntegration = store.children?.find(
              (i) => i.type === integrationType.id.toUpperCase()
            );

            const description = `Pulling data from ${connectedIntegration?.settings?.sources?.length || "0"} ${pluralize("source", connectedIntegration?.settings?.sources?.length)}`;
            return (
              <IntegrationCard
                key={integrationType.id}
                integration={connectedIntegration}
                description={description}
                type={integrationType}
                onConnect={() => handleIntegrationConnect(integrationType)}
                loading={loading}
                isAdmin={isAdmin}
                onManage={() => onManage(connectedIntegration)}
              />
            );
          })}
        </div>
      </SettingsSection>

      <MetaConnectionDialog
        key={store.id}
        open={metaConnectOpen}
        onOpenChange={setMetaConnectOpen}
        parentId={store.id}
      />

      <ManageModal
        integration={manageIntegration}
        open={!!manageIntegration}
        setOpen={() => {
          setManageIntegration(null);
          clearManageParam?.();
        }}
        descriptionRender={(sources) =>
          !sources?.length ? (
            "No sources found. Make sure you have an active campaign."
          ) : (
            <>
              Found {sources.length} source(s). Select the one you want to use.
            </>
          )
        }
      />
    </>
  );
}
